import classNames from 'classnames';
import { Link } from 'react-router-dom';
import { BaseCard } from '~/components/Card';
import { ChallengeTipBanner } from '~/components/Challenge/Banner/ChallengeTipBanner';
import { useChallengeBoost } from '~/components/Challenge/Boost/ChallengeBoost.hooks';
import { useChallengeLeaderboard } from '~/components/Challenge/Leaderboard/ChallengeLeaderboard.hooks';
import { T } from '~/components/I18n';
import { Boost2D } from '~/components/Icon';
import profile from '~/components/Icon/default-profile.png';
import { Chevron } from '~/components/Icon/v2/Chevron';
import { format, formatBoost } from '~/components/Util/number';
import styles from './ChallengeVersus.module.scss';

export type ChallengeVersusProps = {
  className?: string;
  eventId?: number;
};

export const ChallengeVersus = ({ className, eventId }: ChallengeVersusProps) => {
  const { data, isFetched, isFetchedAfterMount } = useChallengeLeaderboard({
    week: eventId,
  });
  const {
    weekStatus,
    myScore,
    myRank,
    myProfileImageUrl,
    rivalProfileImageUrl,
    rivalRank,
    rivalScore,
  } = data;
  const {
    data: { totalBoost },
    isInitialLoading,
  } = useChallengeBoost();

  return (
    <BaseCard
      color="muted"
      className={classNames(
        styles.root,
        className,
        { [styles.loading]: !isFetched },
        styles[weekStatus]
      )}
      size="large">
      <div className={styles.body}>
        <Link to="/challenge" data-event="click" data-event-target="score_history_in_leaderboard">
          <div className={classNames(styles.user, styles.me)}>
            <div className={styles.thumbnail}>
              <img src={myProfileImageUrl} width={100} alt="me" />
            </div>
            <div className={styles.info}>
              <p className={styles.score}>
                <strong className={styles.ranking}>
                  <T
                    name="key_ordinal"
                    options={{ count: myRank, rank: myRank || '-', ordinal: true }}
                  />
                </strong>
              </p>
              <p className={styles.point}>
                <T
                  name="citizen_unit_score"
                  options={[format(myScore, { zeroAsEmpty: !isFetchedAfterMount })]}
                />
                <Chevron className={styles.pointChevron} />
              </p>
              {weekStatus === 'IN_PROGRESS' && (
                <span className={styles.boost}>
                  <Boost2D className={styles.boostIcon} />
                  {formatBoost(totalBoost, { zeroAsEmpty: isInitialLoading })}%
                </span>
              )}
            </div>
          </div>
        </Link>
        <div className={styles.vs}>
          <svg
            width="22"
            height="14"
            viewBox="0 0 22 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg">
            <path
              d="M6.16504 9.88867L8.40625 2.33008C8.6875 1.31055 9.11816 0.897461 9.8916 0.897461C10.7266 0.897461 11.3154 1.41602 11.3154 2.18066C11.3154 2.4707 11.2188 2.91895 11.0869 3.33203L8.49414 11.04C8.02832 12.4814 7.3252 13.0879 6.06836 13.0879C4.79395 13.0879 4.06445 12.4727 3.60742 11.04L1.01465 3.33203C0.865234 2.89258 0.786133 2.48828 0.786133 2.20703C0.786133 1.43359 1.38379 0.897461 2.21875 0.897461C2.96582 0.897461 3.44922 1.36328 3.73926 2.33008L6.0332 9.88867H6.16504ZM12.3438 10.5303C12.3438 9.82715 12.8535 9.33496 13.4775 9.33496C13.8994 9.33496 14.2598 9.49316 14.7432 9.92383C15.4639 10.6006 16.334 10.9697 17.2217 10.9697C18.4785 10.9697 19.1553 10.5127 19.1553 9.68652C19.1553 9.01855 18.6279 8.60547 17.3623 8.33301L15.71 7.95508C13.5479 7.49805 12.4844 6.38184 12.4844 4.54492C12.4844 2.24219 14.2773 0.783203 17.0898 0.783203C18.6279 0.783203 20.0166 1.23145 20.8252 1.99609C21.3438 2.4707 21.6162 2.99805 21.6162 3.53418C21.6162 4.17578 21.1592 4.61523 20.4824 4.61523C20.1045 4.61523 19.7266 4.44824 19.3574 4.09668C18.7422 3.41992 18.0303 3.06836 17.125 3.06836C16.0615 3.06836 15.3936 3.55176 15.3936 4.30762C15.3936 4.9668 15.9033 5.43262 17.0195 5.65234L18.6895 6.0127C20.9219 6.46094 22.0029 7.55957 22.0029 9.37891C22.0029 11.7432 20.0781 13.2637 17.0723 13.2637C15.4199 13.2637 13.9521 12.7891 13.1348 12.0156C12.625 11.5322 12.3438 11.0225 12.3438 10.5303Z"
              fill="black"
            />
          </svg>
        </div>
        <div className={styles.user}>
          <div className={styles.thumbnail}>
            <img src={rivalProfileImageUrl || profile} width={100} alt="rival" />
          </div>
          <div className={styles.info}>
            <p className={styles.score}>
              <strong className={styles.ranking}>
                <T
                  name="key_ordinal"
                  options={{ count: rivalRank, rank: rivalRank || '-', ordinal: true }}
                />
              </strong>
            </p>
            <p className={styles.point}>
              <T
                name="citizen_unit_score"
                options={[format(rivalScore, { zeroAsEmpty: !rivalRank || !isFetchedAfterMount })]}
              />
            </p>
          </div>
        </div>
      </div>
      {weekStatus === 'IN_PROGRESS' && (
        <div className={styles.footer}>
          <ChallengeTipBanner {...data} />
        </div>
      )}
    </BaseCard>
  );
};
